import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { NotificationService } from '../../notification/notification.service';
import { getCalendarDayDifference } from '../../utils/order-deadline.util';
import {
  ProductionService,
  SupportService,
  SupportTicket,
  WorktimeEntry,
  WorktimeService,
} from '../api';

// ============================================================================
// Mai nap — dashboard. Design: erp-design/screens/16-mai-nap.html,
// logika: 23-logic.js dashboardVals() — valós adatra igazítva:
//  - Mai feladás / Késésben / Ma feladandó lista / Hiányzó számla:
//    GET orders/dashboard-summary (szerveroldali aggregáció a teljes állományon)
//  - Gyártási sor + Sürgős gyártás: GET production/queue
//  - Készlet riasztások: GET components/stock (+has_bom=1)
//  - Support: GET support/tickets
//  - Munkaidő gyorspanel: GET/POST worktime/entries
// Minden hívás hibája/404-e üres állapotra esik vissza, a képernyő nem törik.
// ============================================================================

/** GET orders/dashboard-summary — szerveroldali aggregáció a teljes állományon. */
interface DashboardSummary {
  /** aktív, csomagolatlan, még fel nem adott, ma esedékes rendelés */
  todayCount: number;
  /** aktív, csomagolatlan, még fel nem adott, lejárt határidejű rendelés */
  lateCount: number;
  /** aktív, számla nélküli rendelés */
  missingInvoiceCount: number;
  /** todayCount + lateCount (a lista teljes mérete) */
  shipTotal: number;
  /** a legsürgősebb N feladandó rendelés (késők elöl) */
  ship: any[];
}

interface StockApiRow {
  id: number;
  name: string;
  sku: string;
  limit: number;
  /** elérhető készlet (foglalások levonva) */
  stock: number;
  /** aktív rendelések által foglalt mennyiség */
  reserved: number;
}

type KpiTone = 'default' | 'yellow' | 'red' | 'green';

interface DashKpi {
  label: string;
  value: string;
  sub: string;
  tone: KpiTone;
  link: string;
}

interface DashShipRow {
  orderId: number;
  name: string;
  idLabel: string;
  place: string;
  late: boolean;
  chipLabel: string;
  hasStockIssue: boolean;
}

interface DashDotRow {
  name: string;
  sub: string;
  red: boolean;
  need?: string;
}

interface DashSupRow {
  id: number;
  title: string;
  prioLabel: string;
  prioColor: string;
  statusLabel: string;
  avatarInitial: string;
  avatarColor: string;
  avatarTitle: string;
}

type WtState = 'none' | 'progress' | 'done' | 'off';

// 23-logic.js supUsers / prioDash színei
const SUP_USER_COLORS: Record<string, string> = {
  'Virág': '#C77B9B',
  'Bence': '#5E7FA8',
  'Peti': '#6E9663',
};
const SUP_PRIO_COLORS: Record<string, string> = {
  P1: '#C97C7C',
  P2: '#C9A45C',
  P3: '#B4BAA6',
};

const HU_MONTHS = ['január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];
const HU_WEEKDAYS = ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'];

@Component({
  selector: 'app-mai-nap',
  templateUrl: './mai-nap.component.html',
  styleUrl: './mai-nap.component.scss'
})
export class MaiNapComponent implements OnInit {

  dashDate = '';

  // KPI kártyák
  kpis: DashKpi[] = [];
  private todayCount = 0;
  private lateCount = 0;
  private mfgNeed = 0;
  private mfgCount = 0;
  private invMissing = 0;
  private supOpenCount = 0;
  private supCriticalCount = 0;
  private supNewCount = 0;

  // Ma feladandó rendelések
  shipRows: DashShipRow[] = [];
  shipMore = '';
  ordersLoaded = false;
  private shipSource: { order: any; days: number }[] = [];
  private urgentSkus = new Set<string>();

  // Sürgős gyártás
  mfgRows: DashDotRow[] = [];
  mfgMore = '';
  mfgLoaded = false;

  // Készlet riasztások
  lowRows: DashDotRow[] = [];
  lowLoaded = false;

  // Számlázás chip
  get invLabel(): string {
    return this.invMissing > 0
      ? `${this.invMissing} rendeléshez hiányzik számla`
      : 'Minden rendeléshez van számla';
  }
  get invDanger(): boolean {
    return this.invMissing > 0;
  }

  // Support — sürgős
  supRows: DashSupRow[] = [];
  supMore = '';
  supLoaded = false;

  // Munkaidő — ma
  wtState: WtState = 'none';
  wtStartedLabel = '';
  wtDoneLabel = '';
  wtOffLabel = '';
  wtSaving = false;
  private wtEntry: WorktimeEntry | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private production: ProductionService,
    private support: SupportService,
    private worktime: WorktimeService,
    private notification: NotificationService,
  ) {}

  ngOnInit(): void {
    this.dashDate = this.buildDashDate(new Date());
    this.rebuildKpis();
    this.fetchDashboardSummary();
    this.fetchQueue();
    this.fetchLowStock();
    this.fetchSupport();
    this.fetchWorktime();
  }

  // --------------------------------------------------------------------------
  // Fejléc dátum — '2026. július 7., kedd'
  // --------------------------------------------------------------------------

  private buildDashDate(d: Date): string {
    return `${d.getFullYear()}. ${HU_MONTHS[d.getMonth()]} ${d.getDate()}., ${HU_WEEKDAYS[d.getDay()]}`;
  }

  // --------------------------------------------------------------------------
  // Rendelések — mai feladás + késés + feladandó lista + hiányzó számla
  // --------------------------------------------------------------------------

  /**
   * Dashboard összesítő — a szerver a TELJES állományon aggregál.
   * (Korábban egy 200 elemű rendelés-oldalt kértünk le és abból számoltuk a
   * KPI-kat: a "Mai feladás" ezért mindig 0 volt, a "Késésben" pontosan a
   * lapméretet mutatta, a mai rendelések pedig ki sem kerültek a listába.)
   */
  private fetchDashboardSummary(): void {
    this.http.get<DashboardSummary>('orders/dashboard-summary?limit=12').subscribe({
      next: (summary) => {
        this.todayCount = summary?.todayCount || 0;
        this.lateCount = summary?.lateCount || 0;
        this.invMissing = summary?.missingInvoiceCount || 0;
        const ship = summary?.ship || [];
        this.shipSource = ship
          .map((order) => ({ order, days: getCalendarDayDifference(order?.shipping_expected_at) }))
          .filter((x): x is { order: any; days: number } => x.days !== null);
        const shown = this.shipSource.length;
        const total = summary?.shipTotal || shown;
        this.shipMore = total > shown ? `+${total - shown} további feladandó rendelés` : '';
        this.ordersLoaded = true;
        this.rebuildShipRows();
        this.rebuildKpis();
      },
      error: () => {
        this.todayCount = 0;
        this.lateCount = 0;
        this.invMissing = 0;
        this.shipSource = [];
        this.shipMore = '';
        this.ordersLoaded = true;
        this.rebuildShipRows();
        this.rebuildKpis();
      },
    });
  }

  private rebuildShipRows(): void {
    this.shipRows = this.shipSource.map(({ order, days }) => ({
      orderId: order?.id,
      name: order?.customer || 'Ismeretlen vevő',
      idLabel: `#${order?.receipt_id}`,
      place: `${order?.shipping_city || order?.address || '–'} · ${order?.country_iso || order?.shipping_country || '–'}`,
      late: days < 0,
      chipLabel: days < 0 ? `${Math.abs(days)} napja késik` : 'ma kell feladni',
      hasStockIssue: this.orderHasStockIssue(order),
    }));
  }

  /** Készlethiány jelzés: a rendelés terméke szerepel a sürgős gyártási sorban. */
  private orderHasStockIssue(order: any): boolean {
    if (this.urgentSkus.size === 0) {
      return false;
    }
    const products: any[] = Array.isArray(order?.orderProducts) ? order.orderProducts : [];
    return products.some((product) => {
      const sku = product?.component?.sku || product?.sku;
      return typeof sku === 'string' && this.urgentSkus.has(sku);
    });
  }

  // --------------------------------------------------------------------------
  // Gyártási sor (production/queue)
  // --------------------------------------------------------------------------

  private fetchQueue(): void {
    this.production.getQueue().subscribe({
      next: (queue) => {
        const tasks = (queue?.tasks || []).filter((t) => (t.need || 0) > 0);
        // 23-logic.js rendezés: sürgős, elfogyott
        tasks.sort((a, b) =>
          ((b.urgent ? 1 : 0) - (a.urgent ? 1 : 0))
          || ((b.out ? 1 : 0) - (a.out ? 1 : 0)));
        this.mfgCount = tasks.length;
        this.mfgNeed = tasks.reduce((sum, t) => sum + (t.need || 0), 0);
        this.mfgRows = tasks.slice(0, 3).map((t) => ({
          name: t.name,
          need: `${t.need} db`,
          sub: `${t.stock || 0} / ${t.target} db készleten`,
          red: !!(t.urgent || t.out),
        }));
        this.mfgMore = tasks.length > 3 ? `+${tasks.length - 3} további feladat` : '';
        this.urgentSkus = new Set(tasks.filter((t) => t.urgent).map((t) => t.sku));
        this.mfgLoaded = true;
        this.rebuildShipRows();
        this.rebuildKpis();
      },
      error: () => {
        this.mfgCount = 0;
        this.mfgNeed = 0;
        this.mfgRows = [];
        this.mfgMore = '';
        this.mfgLoaded = true;
        this.rebuildKpis();
      },
    });
  }

  // --------------------------------------------------------------------------
  // Készlet riasztások (components/stock)
  // --------------------------------------------------------------------------

  private fetchLowStock(): void {
    // A stock végpont has_bom nélkül csak a nyers komponenseket adja,
    // az összeépítetteket külön kérjük (mint a Komponensek képernyő).
    forkJoin({
      raw: this.http.get<StockApiRow[]>('components/stock'),
      assembled: this.http.get<StockApiRow[]>('components/stock?has_bom=1'),
    }).subscribe({
      next: ({ raw, assembled }) => {
        const all = [...(raw || []), ...(assembled || [])];
        // 23-logic.js: stock < minStock, készlet-arány szerint növekvő
        const low = all
          .map((c) => ({
            name: c.name,
            stock: (c.stock || 0) + (c.reserved || 0),
            minStock: c.limit || 0,
          }))
          .filter((c) => c.stock < c.minStock)
          .sort((a, b) => a.stock / Math.max(1, a.minStock) - b.stock / Math.max(1, b.minStock));
        this.lowRows = low.slice(0, 3).map((c) => ({
          name: c.name,
          sub: `${c.stock} db · min. ${c.minStock}`,
          red: c.stock <= 0,
        }));
        this.lowLoaded = true;
      },
      error: () => {
        this.lowRows = [];
        this.lowLoaded = true;
      },
    });
  }

  // --------------------------------------------------------------------------
  // Support — sürgős
  // --------------------------------------------------------------------------

  private fetchSupport(): void {
    this.support.getTickets().subscribe({
      next: (tickets) => {
        const open = (tickets || []).filter((t) => !t.isDone && t.status !== 'Megoldva');
        this.supOpenCount = open.length;
        this.supCriticalCount = open.filter((t) => t.priority === 'P1').length;
        this.supNewCount = open.filter((t) => t.status === 'Új').length;
        const prioRank: Record<string, number> = { P1: 0, P2: 1, P3: 2 };
        const top = [...open].sort((a, b) =>
          ((prioRank[a.priority] ?? 3) - (prioRank[b.priority] ?? 3))
          || ((a.status === 'Új' ? -1 : 0) - (b.status === 'Új' ? -1 : 0)))
          .slice(0, 3);
        this.supRows = top.map((t) => this.buildSupRow(t));
        this.supMore = open.length > 3 ? `+${open.length - 3} további nyitott task` : '';
        this.supLoaded = true;
        this.rebuildKpis();
      },
      error: () => {
        this.supOpenCount = 0;
        this.supCriticalCount = 0;
        this.supNewCount = 0;
        this.supRows = [];
        this.supMore = '';
        this.supLoaded = true;
        this.rebuildKpis();
      },
    });
  }

  private buildSupRow(ticket: SupportTicket): DashSupRow {
    const creator = ticket.creator || '?';
    return {
      id: ticket.id,
      title: ticket.title || 'Névtelen task',
      prioLabel: ticket.priority,
      prioColor: SUP_PRIO_COLORS[ticket.priority] || '#B4BAA6',
      statusLabel: ticket.status,
      avatarInitial: creator.slice(0, 1).toUpperCase(),
      avatarColor: SUP_USER_COLORS[creator] || '#9AA098',
      avatarTitle: creator,
    };
  }

  // --------------------------------------------------------------------------
  // Munkaidő — ma
  // --------------------------------------------------------------------------

  private fetchWorktime(): void {
    const now = new Date();
    this.worktime.getEntries(now.getFullYear(), now.getMonth() + 1).subscribe({
      next: (entries) => {
        const today = this.todayKey();
        this.applyWtEntry((entries || []).find((e) => e.date === today) || null);
      },
      error: () => {
        this.applyWtEntry(null);
      },
    });
  }

  private todayKey(): string {
    const d = new Date();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }

  private applyWtEntry(entry: WorktimeEntry | null): void {
    this.wtEntry = entry;
    if (!entry) {
      this.wtState = 'none';
      return;
    }
    if (entry.type !== 'work') {
      this.wtState = 'off';
      this.wtOffLabel = entry.type === 'vacation' ? 'Ma szabadságon vagy.' : 'Ma betegszabadságon vagy.';
      return;
    }
    if (!entry.endTime) {
      this.wtState = 'progress';
      this.wtStartedLabel = `Dolgozol ${entry.startTime || '–'} óta`;
      return;
    }
    this.wtState = 'done';
    const hours = entry.workedHours ?? this.wtHours(entry.startTime, entry.endTime);
    this.wtDoneLabel = `${entry.startTime} – ${entry.endTime} · ${this.wtHoursLabel(hours)}`;
  }

  /** 23-logic.js wtHours: (vége - kezdés) - 30 perc ebédszünet. */
  private wtHours(start: string | null, end: string | null): number {
    if (!start || !end) {
      return 0;
    }
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    if (isNaN(sh) || isNaN(eh)) {
      return 0;
    }
    const hours = (eh * 60 + (em || 0) - sh * 60 - (sm || 0)) / 60 - 0.5;
    return Math.max(0, Math.round(hours * 100) / 100);
  }

  /** 23-logic.js wtHL: '7,5 ó' formátum. */
  private wtHoursLabel(hours: number): string {
    const rounded = Math.round(hours * 10) / 10;
    return (rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)) + ' ó';
  }

  private nowTime(): string {
    return new Date().toTimeString().slice(0, 5);
  }

  startWork(): void {
    if (this.wtSaving) {
      return;
    }
    this.wtSaving = true;
    const startTime = this.nowTime();
    this.worktime.saveEntry({
      date: this.todayKey(),
      type: 'work',
      startTime,
      endTime: null,
      comment: '',
    }).subscribe({
      next: (entry) => {
        this.wtSaving = false;
        this.applyWtEntry(entry || {
          id: 0, userId: 0, date: this.todayKey(), type: 'work',
          startTime, endTime: null, comment: '', workedHours: null,
        });
      },
      error: () => {
        this.wtSaving = false;
        this.notification.show('A munkaidő indítása nem sikerült.', 'danger');
      },
    });
  }

  stopWork(): void {
    if (this.wtSaving || !this.wtEntry) {
      return;
    }
    this.wtSaving = true;
    const endTime = this.nowTime();
    const startTime = this.wtEntry.startTime;
    this.worktime.saveEntry({
      date: this.todayKey(),
      type: 'work',
      startTime,
      endTime,
      comment: this.wtEntry.comment || '',
    }).subscribe({
      next: (entry) => {
        this.wtSaving = false;
        this.applyWtEntry(entry || {
          id: 0, userId: 0, date: this.todayKey(), type: 'work',
          startTime, endTime, comment: '', workedHours: null,
        });
      },
      error: () => {
        this.wtSaving = false;
        this.notification.show('A munkaidő zárása nem sikerült.', 'danger');
      },
    });
  }

  // --------------------------------------------------------------------------
  // KPI kártyák
  // --------------------------------------------------------------------------

  private rebuildKpis(): void {
    const supSub = (this.supCriticalCount > 0 ? `${this.supCriticalCount} kritikus · ` : '')
      + `${this.supNewCount} új`;
    this.kpis = [
      {
        label: 'Mai feladás', value: `${this.todayCount} db`, sub: 'csomagolásra vár',
        tone: this.todayCount > 0 ? 'yellow' : 'default', link: '/orders',
      },
      {
        label: 'Késésben', value: `${this.lateCount} db`, sub: 'lejárt feladási határidő',
        tone: this.lateCount > 0 ? 'red' : 'green', link: '/orders',
      },
      {
        label: 'Gyártási sor', value: `${this.mfgNeed} db`, sub: `${this.mfgCount} nyomtatási feladat`,
        tone: 'default', link: '/gyartas',
      },
      {
        label: 'Hiányzó számla', value: `${this.invMissing} db`, sub: 'kiállításra vár',
        tone: this.invMissing > 0 ? 'red' : 'green', link: '/szamlazas',
      },
      {
        label: 'Support', value: `${this.supOpenCount} db`, sub: supSub,
        tone: this.supCriticalCount > 0 ? 'red' : 'default', link: '/support',
      },
    ];
  }

  // --------------------------------------------------------------------------
  // Navigáció
  // --------------------------------------------------------------------------

  openKpi(kpi: DashKpi): void {
    this.router.navigateByUrl(kpi.link);
  }

  goOrders(): void {
    this.router.navigate(['/orders']);
  }

  goProduction(): void {
    this.router.navigate(['/gyartas']);
  }

  goComponents(): void {
    this.router.navigate(['/components']);
  }

  goBilling(): void {
    this.router.navigate(['/szamlazas']);
  }

  goSupport(): void {
    this.router.navigate(['/support']);
  }

  goWorktime(): void {
    this.router.navigate(['/munkaido']);
  }

  openOrder(orderId: number): void {
    if (orderId) {
      this.router.navigate(['/orders', orderId]);
    }
  }

  openSupportTicket(row: DashSupRow): void {
    this.router.navigate(['/support'], { queryParams: { ticket: row.id } });
  }

  // --------------------------------------------------------------------------
  // trackBy
  // --------------------------------------------------------------------------

  trackByShip(_: number, row: DashShipRow) {
    return row.orderId;
  }

  trackBySup(_: number, row: DashSupRow) {
    return row.id;
  }

  trackByIndex(index: number) {
    return index;
  }
}
